import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { FaTimes, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { createOrder } from '../../../../store/actions/orders';
import AddressAutocompleteInput from '../../../../components/AddressAutocomplete';
import { nigeriaStates } from '../../../../utils/nigeriaData';
import Container from './styles';

const OrderForm = ({ showModal, onClose, onSuccess, editOrder }) => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submissionState, setSubmissionState] = useState('form'); // 'form', 'loading', 'success', 'error'
  const [orderData, setOrderData] = useState(null);
  const [pickupAddressFocus, setPickupAddressFocus] = useState(false);
  const [deliveryAddressFocus, setDeliveryAddressFocus] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: editOrder?.customer_name || '',
    customer_phone: editOrder?.customer_phone || '',
    pickup_state: editOrder?.pickup_state || '',
    pickup_address: editOrder?.pickup_address || '',
    pickup_lat: editOrder?.pickup_lat || null,
    pickup_lng: editOrder?.pickup_lng || null,
    delivery_state: editOrder?.delivery_state || '',
    delivery_address: editOrder?.delivery_address || '',
    delivery_lat: editOrder?.delivery_lat || null,
    delivery_lng: editOrder?.delivery_lng || null,
    package_type: editOrder?.package_type || 'standard',
    package_weight: editOrder?.package_weight || '',
    package_description: editOrder?.package_description || '',
    notes: editOrder?.notes || '',
  });

  if (!showModal) return null;

  const totalSteps = 5;

  const steps = [
    { number: 1, title: 'Customer Info', description: 'Enter customer details' },
    { number: 2, title: 'Pickup', description: 'Pickup location details' },
    { number: 3, title: 'Delivery', description: 'Delivery location details' },
    { number: 4, title: 'Package', description: 'Package information' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updates = { [name]: value };
      
      // Auto-focus address field after state selection
      if (name === 'pickup_state' && value && currentStep === 2) {
        setPickupAddressFocus(true);
        setTimeout(() => setPickupAddressFocus(false), 100);
      } else if (name === 'delivery_state' && value && currentStep === 3) {
        setDeliveryAddressFocus(true);
        setTimeout(() => setDeliveryAddressFocus(false), 100);
      }
      
      return { ...prev, ...updates };
    });
  };

  const handlePickupPlaceSelected = (placeData) => {
    setFormData(prev => ({
      ...prev,
      pickup_address: placeData.address,
      pickup_lat: placeData.lat,
      pickup_lng: placeData.lng,
    }));
  };

  const handleDeliveryPlaceSelected = (placeData) => {
    setFormData(prev => ({
      ...prev,
      delivery_address: placeData.address,
      delivery_lat: placeData.lat,
      delivery_lng: placeData.lng,
    }));
  };

  const validateStep = () => {
    console.log('Validating step:', currentStep);
    console.log('Current form data:', formData);
    
    switch (currentStep) {
      case 1:
        console.log('Step 1 validation:', {
          customer_name: formData.customer_name,
          customer_phone: formData.customer_phone
        });
        if (!formData.customer_name) {
          alert('Please enter customer name');
          return false;
        }
        if (!formData.customer_phone) {
          alert('Please enter customer phone');
          return false;
        }
        return true;
        
      case 2:
        console.log('Step 2 validation:', {
          pickup_state: formData.pickup_state,
          pickup_address: formData.pickup_address,
          pickup_lat: formData.pickup_lat,
          pickup_lng: formData.pickup_lng
        });
        if (!formData.pickup_state) {
          alert('Please select pickup state');
          return false;
        }
        if (!formData.pickup_address) {
          alert('Please enter pickup address');
          return false;
        }
        if (!formData.pickup_lat || !formData.pickup_lng) {
          alert('Please select an address from the dropdown to capture location coordinates');
          return false;
        }
        return true;
        
      case 3:
        console.log('Step 3 validation:', {
          delivery_state: formData.delivery_state,
          delivery_address: formData.delivery_address,
          delivery_lat: formData.delivery_lat,
          delivery_lng: formData.delivery_lng
        });
        if (!formData.delivery_state) {
          alert('Please select delivery state');
          return false;
        }
        if (!formData.delivery_address) {
          alert('Please enter delivery address');
          return false;
        }
        if (!formData.delivery_lat || !formData.delivery_lng) {
          alert('Please select an address from the dropdown to capture location coordinates');
          return false;
        }
        return true;
        
      case 4:
        console.log('Step 4 validation:', {
          package_type: formData.package_type
        });
        if (!formData.package_type) {
          alert('Please select package type');
          return false;
        }
        return true;
        
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
    // Validation messages are shown in validateStep()
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Form submitted, current step:', currentStep, 'total steps:', totalSteps);
    
    // Only submit if on the final step
    if (currentStep !== totalSteps) {
      console.log('Not on final step, calling handleNext instead');
      handleNext();
      return;
    }
    
    if (!validateStep()) {
      console.log('Validation failed');
      return;
    }

    console.log('Creating order with data:', formData);
    setLoading(true);
    setSubmissionState('loading');

    try {
      // Transform form data to match backend DTO (camelCase)
      const orderPayload = {
        customerName: formData.customer_name,
        customerPhone: formData.customer_phone,
        pickupAddress: formData.pickup_address,
        pickupLat: formData.pickup_lat,
        pickupLng: formData.pickup_lng,
        deliveryAddress: formData.delivery_address,
        deliveryLat: formData.delivery_lat,
        deliveryLng: formData.delivery_lng,
        packageDescription: formData.package_description || `${formData.package_type} package`,
        packageWeight: formData.package_weight || undefined,
        packageSize: formData.package_weight ? 
          (parseFloat(formData.package_weight) > 10 ? 'LARGE' : 
           parseFloat(formData.package_weight) > 5 ? 'MEDIUM' : 'SMALL') 
          : 'MEDIUM',
        notes: formData.notes || undefined,
      };

      const result = await dispatch(createOrder(orderPayload));
      
      if (result && result.success) {
        setOrderData(result.data);
        setSubmissionState('success');
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        setSubmissionState('error');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      setSubmissionState('error');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step--content">
            <div className="step--header">
              <h3>Customer Information</h3>
              <p>Who is this delivery for?</p>
            </div>
            <div className="form--grid">
              <div className="form--group full-width">
                <label>Customer Name *</label>
                <input
                  type="text"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  placeholder="Enter customer name"
                  required
                />
              </div>
              <div className="form--group full-width">
                <label>Customer Phone *</label>
                <input
                  type="tel"
                  name="customer_phone"
                  value={formData.customer_phone}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  required
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step--content">
            <div className="step--header">
              <h3>Pickup Location</h3>
              <p>Where should we pick up the package?</p>
            </div>
            <div className="form--grid">
              <div className="form--group">
                <label>State *</label>
                <select
                  name="pickup_state"
                  value={formData.pickup_state}
                  onChange={handleChange}
                  required
                >
                  <option  value="">Select State</option>
                  {nigeriaStates.map((state) => (
                    <option style={{color:"#000"}} key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form--group full-width">
                <label>Street Address *</label>
                <AddressAutocompleteInput
                  name="pickup_address"
                  value={formData.pickup_address}
                  onChange={handleChange}
                  onPlaceSelected={handlePickupPlaceSelected}
                  placeholder={
                    formData.pickup_state
                      ? `Search address in ${formData.pickup_state}, Nigeria`
                      : "Select state first"
                  }
                  required
                  disabled={!formData.pickup_state}
                  autoFocus={pickupAddressFocus}
                  stateFilter={formData.pickup_state}
                />
                {formData.pickup_lat && formData.pickup_lng && (
                  <small style={{ color: '#10b981', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    ✓ Location coordinates captured
                  </small>
                )}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step--content">
            <div className="step--header">
              <h3>Delivery Location</h3>
              <p>Where should we deliver the package?</p>
            </div>
            <div className="form--grid">
              <div className="form--group">
                <label>State *</label>
                <select
                  name="delivery_state"
                  value={formData.delivery_state}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select State</option>
                  {nigeriaStates.map((state) => (
                    <option style={{color:"#000"}} key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form--group full-width">
                <label>Street Address *</label>
                <AddressAutocompleteInput
                  name="delivery_address"
                  value={formData.delivery_address}
                  onChange={handleChange}
                  onPlaceSelected={handleDeliveryPlaceSelected}
                  placeholder={
                    formData.delivery_state
                      ? `Search address in ${formData.delivery_state}, Nigeria`
                      : "Select state first"
                  }
                  required
                  disabled={!formData.delivery_state}
                  autoFocus={deliveryAddressFocus}
                  stateFilter={formData.delivery_state}
                />
                {formData.delivery_lat && formData.delivery_lng && (
                  <small style={{ color: '#10b981', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    ✓ Location coordinates captured
                  </small>
                )}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="step--content">
            <div className="step--header">
              <h3>Package Details</h3>
              <p>Tell us about the package</p>
            </div>
            
            <div className="form--grid">
              <div className="form--group">
                <label>Package Type *</label>
                <select
                  name="package_type"
                  value={formData.package_type}
                  onChange={handleChange}
                  required
                >
                  <option style={{color:"#000"}} value="standard">Standard</option>
                  <option style={{color:"#000"}} value="fragile">Fragile</option>
                  <option style={{color:"#000"}} value="perishable">Perishable</option>
                  <option style={{color:"#000"}} value="electronics">Electronics</option>
                  <option style={{color:"#000"}} value="documents">Documents</option>
                </select>
              </div>
              <div className="form--group">
                <label>Weight (kg)</label>
                <input
                  type="number"
                  name="package_weight"
                  value={formData.package_weight}
                  onChange={handleChange}
                  step="0.1"
                  min="0"
                  placeholder="0.0"
                />
              </div>
              <div className="form--group full-width">
                <label>Description</label>
                <textarea
                  name="package_description"
                  value={formData.package_description}
                  onChange={handleChange}
                  rows="4"
                  placeholder="What's inside the package?"
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Loading Screen
  if (submissionState === 'loading') {
    return (
      <Container onClick={(e) => e.stopPropagation()}>
        <div className="modal--content" onClick={(e) => e.stopPropagation()}>
          <div className="loading--screen">
            <div className="spinner--wrapper">
              <div className="spinner"></div>
            </div>
            <h3>Creating Your Order...</h3>
            <p>Please wait while we process your request</p>
          </div>
        </div>
      </Container>
    );
  }

  // Success Screen
  if (submissionState === 'success') {
    return (
      <Container onClick={(e) => e.stopPropagation()}>
        <div className="modal--content" onClick={(e) => e.stopPropagation()}>
          <div className="success--screen">
            <div className="success--icon">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                <circle cx="40" cy="40" r="40" fill="#10b981" fillOpacity="0.2"/>
                <circle cx="40" cy="40" r="32" fill="#10b981"/>
                <path d="M25 40L35 50L55 30" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Order Created Successfully!</h3>
            <p>Your order has been placed and is being processed</p>
            {orderData && orderData.trackingNumber && (
              <div className="tracking--info">
                <span>Tracking Number:</span>
                <strong>{orderData.trackingNumber}</strong>
              </div>
            )}
          </div>
        </div>
      </Container>
    );
  }

  // Error Screen
  if (submissionState === 'error') {
    return (
      <Container onClick={(e) => e.stopPropagation()}>
        <div className="modal--content" onClick={(e) => e.stopPropagation()}>
          <div className="error--screen">
            <div className="error--icon">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                <circle cx="40" cy="40" r="40" fill="#ef4444" fillOpacity="0.2"/>
                <circle cx="40" cy="40" r="32" fill="#ef4444"/>
                <path d="M30 30L50 50M50 30L30 50" stroke="white" strokeWidth="4" strokeLinecap="round"/>
              </svg>
            </div>
            <h3>Order Creation Failed</h3>
            <p>Something went wrong. Please try again.</p>
            <button className="btn btn--primary" onClick={() => setSubmissionState('form')}>
              Try Again
            </button>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container onClick={onClose}>
      <div className="modal--content" onClick={(e) => e.stopPropagation()}>
        <div className="modal--header">
          <div>
            <h2>{editOrder ? 'Edit Order' : 'Create New Order'}</h2>
            <p className="step--indicator">Step {currentStep} of {totalSteps}</p>
          </div>
          <button className="close--btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="steps--indicator">
          {steps.map((step) => (
            <div 
              key={step.number} 
              className={`step--item ${currentStep >= step.number ? 'active' : ''} ${currentStep === step.number ? 'current' : ''}`}
            >
              <div className="step--number">{step.number}</div>
              <div className="step--info">
                <div className="step--title">{step.title}</div>
                <div className="step--description">{step.description}</div>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal--body">
            {renderStepContent()}
          </div>

          <div className="modal--footer">
            <div className="footer--left">
              {currentStep > 1 && (
                <button type="button" className="btn btn--secondary" onClick={handleBack}>
                  <FaArrowLeft className="btn-icon" />
                  Back
                </button>
              )}
            </div>
            <div className="footer--right">
              <button type="button" className="btn btn--secondary" onClick={onClose}>
                Cancel
              </button>
              {currentStep < totalSteps ? (
                <button type="button" className="btn btn--primary" onClick={handleNext}>
                  Next
                  <FaArrowRight className="btn-icon" />
                </button>
              ) : (
                <button type="submit" className="btn btn--primary" disabled={loading}>
                  {loading ? 'Creating...' : editOrder ? 'Update Order' : 'Create Order'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </Container>
  );
};

export default OrderForm;
